import { computed, effect, inject, Injectable, signal } from '@angular/core';
import { CartCharges, CartItem, CartItemType, CartSectionType, CartSelectedCustomization } from '../../../../../../models/order/workflow/order-cart.model';
import { OrderFlowType, ProductType } from '../../../../../../models/order/order.model';
import { InternetProductResponseService } from '../../order-api-response/internet-pd-response.service';
import { InternetCartData } from '../../../../../../models/order/workflow/internet/internet-cart.model';
import { OrderCustomizationsStateService } from '../order-customization/order-customization.state.service';
import { OrderStateServiceV2 } from '../../order-state.service';
import { OrderPaymentResponseService } from '../../order-api-response/order-payment.service';
import { InternetPackageSelectionStateService } from '../internet/internet-package-selection/internet-package-selection-state.service';
import { CharterInternetCartStateService } from './order-internet-charter-cart.state.service';
import { BrightspeedInternetCartStateService } from './order-internet-brightspeed-cart.state.service';
import { OOFProvidersEnum } from '../../../../../../enums/oofproviders.enum';
import { isClosingOfferGroup, isInternetAbpEnrolledChoiceCode, isInternetAbpSubGroup } from '../../../../../../constants/app-constants';


@Injectable({ providedIn: 'root' })
export class InternetCartStateService {
    private orderStateService = inject(OrderStateServiceV2);
    private internetPackageStateService = inject(InternetPackageSelectionStateService);
    private internetPdResponseService = inject(InternetProductResponseService);
    private charterInternetCartStateService = inject(CharterInternetCartStateService);
    private brightspeedInternetCartStateService = inject(BrightspeedInternetCartStateService);
    private orderCustomizationsStateService = inject(OrderCustomizationsStateService);
    private paymentService = inject(OrderPaymentResponseService);
    private readonly hideInstallationTimeslotChoiceCodes = new Set(['1-VZUS', '1-WWNJK', '1-VZV5']);
    private selectedCustomizations: CartSelectedCustomization[] = [];
    private _cart = signal<InternetCartData | null>(null);
    private orderCustomizations = this.orderCustomizationsStateService.selectedCustomizations;
    cart = this._cart.asReadonly();
    private isCharter = computed(() => {
        const order = this.orderStateService.order();
        const provider = order?.internetFlow?.provider?.toLowerCase();
        return provider === OOFProvidersEnum.CHARTER || provider === 'spectrum';
    });

    constructor() {
        effect(() => {
            const order = this.orderCustomizations();
            const pdResponse = this.internetPdResponseService.productResponse();
            // Delegate to Charter-specific cart for Charter orders
            if (pdResponse && this.isCharter()) {
                this._cart.set(this.charterInternetCartStateService.cart());
                return;
            }
            this._cart.set(null);
            this.selectedCustomizations = [];
            if (order && this.internetPackageStateService.isPlanSelected()) {
                this.setupSelectedCustomizations();
                this.setupCartFromOrder();
            }
        });
    }

    private setupCartFromOrder() {
        const internetCharges = this.setupInternetCharges();
        if (internetCharges) {
            const firstBillCharges = this.setupFirstBillCharges();
            const todaysCharges = this.setupTodaysCharges();
            const packagesCharges = this.setupPackageCharges();
            const savingAndPromotionCharges = this.setupSavingAndPromotionCharges();
            internetCharges.firstBillCharges = firstBillCharges;
            internetCharges.todaysCharges = todaysCharges;
            internetCharges.packagesCharges = packagesCharges;
            internetCharges.savingAndPromotionCharges = savingAndPromotionCharges;
        }

        this._cart.set(internetCharges);
    }

    private setupSelectedCustomizations() {
        if (!this.orderCustomizations().length) {
            this.selectedCustomizations = [];
            return;
        }
        const orderInternetCustomizations = this.orderCustomizations().filter(c => c.type === OrderFlowType.Internet);
        if (orderInternetCustomizations.length) {
            const selectedCustomizations: CartSelectedCustomization[] = [];
            for (const customization of orderInternetCustomizations) {
                selectedCustomizations.push({ Customization: customization.customization, Choice: customization.choice });
            }

            selectedCustomizations.sort((a, b) => (a.Choice?.CartSeq || 0) - (b.Choice?.CartSeq || 0));
            this.selectedCustomizations = selectedCustomizations;
        }
    }

    private setupFirstBillCharges(): CartCharges | undefined {
        const firstBillCharges: CartCharges = {
            totalAmount: 0,
            items: []
        };

        if (this.selectedCustomizations.length > 0) {
            for (const offer of this.selectedCustomizations) {
                if (offer.Customization.CartCategory?.toLocaleLowerCase() == 'firstbill' || offer.Choice.CartCategory?.toLocaleLowerCase() == 'firstbill') {
                    const cartItem: CartItem = {
                        disclosure: offer.Choice?.CartDisclosure || '',
                        amount: offer.Choice?.Fee.Fee || 0,
                        name: offer.Choice?.CartName || offer.Choice?.Name || '',
                        choiceCode: offer.Choice?.ChoiceCode || '',
                        choice: offer.Choice,
                        customization: offer.Customization,
                        metadata: offer,
                        productType: ProductType.Internet
                    }
                    firstBillCharges.items.push(cartItem);
                    firstBillCharges.totalAmount += cartItem.amount;
                }
            }
        }
        return firstBillCharges;
    }

    private setupTodaysCharges(): CartCharges | undefined {
        const todayCharges: CartCharges = {
            totalAmount: 0,
            items: []
        };

        const todaysOffers = this.brightspeedInternetCartStateService.getTodaysOffers(this.selectedCustomizations);

        if (todaysOffers.length > 0) {
            for (const offer of todaysOffers) {
                if (offer.Customization.CartCategory == 'TodaysCharges' || offer.Choice.CartCategory == 'TodaysCharges') {
                    const cartItem: CartItem = {
                        disclosure: offer.Choice?.CartDisclosure || '',
                        amount: offer.Choice?.Fee.Fee || 0,
                        name: offer.Choice?.CartName || offer.Choice?.Name || '',
                        choiceCode: offer.Choice?.ChoiceCode || '',
                        choice: offer.Choice,
                        customization: offer.Customization,
                        metadata: offer,
                        type: CartItemType.Fee,
                        cartSectionType: CartSectionType.TodaysCharges,
                        productType: ProductType.Internet
                    }
                    todayCharges.items.push(cartItem);
                    todayCharges.totalAmount += cartItem.amount;
                }
            }
        }

        //Check for Advance Bill Payment and add to todays charges
        const abpState = this.orderStateService.order()?.advanceBillPayment;
        const advancePaymentInCart = this.brightspeedInternetCartStateService.isAdvancePaymentInCart(todayCharges);
        if (abpState && abpState.customization && abpState.customization.Choices?.Choice && !advancePaymentInCart) {
            const cartItem: CartItem = {
                disclosure: abpState.customization.Choices?.Choice?.CartDisclosure || '',
                amount: abpState.customization.Choices?.Choice?.Fee.Fee || 0,
                name: abpState.customization.Choices?.Choice?.Name || '',
                choiceCode: abpState.customization.Choices?.Choice?.ChoiceCode || '',
                choice: abpState.customization.Choices?.Choice,
                customization: abpState.customization as any,
                metadata: {} as CartSelectedCustomization,
                type: CartItemType.Fee,
                cartSectionType: CartSectionType.TodaysCharges,
                productType: ProductType.Internet
            };
            todayCharges.items.push(cartItem);
            todayCharges.totalAmount += cartItem.amount;
        }

        return todayCharges;
    }

    private setupPackageCharges(): CartCharges | undefined {
        const packageCharges: CartCharges = {
            totalAmount: 0,
            items: []
        };

        if (this.selectedCustomizations.length > 0) {
            for (const offer of this.selectedCustomizations) {
                if (offer.Choice.CartCategory == 'Package') {
                    const cartItem: CartItem = {
                        disclosure: offer.Choice?.CartDisclosure || '',
                        amount: offer.Choice?.Fee.Fee || 0,
                        name: offer.Choice?.CartName || offer.Choice?.Name || '',
                        choiceCode: offer.Choice?.ChoiceCode || '',
                        choice: offer.Choice,
                        customization: offer.Customization,
                        metadata: offer,
                        type: CartItemType.Fee,
                        cartSectionType: CartSectionType.Packages,
                        productType: ProductType.Internet
                    }
                    packageCharges.items.push(cartItem);
                    packageCharges.totalAmount += cartItem.amount;
                }
            }
        }

        return packageCharges;
    }

    private setupSavingAndPromotionCharges(): CartCharges | undefined {
        const savingAndPromotionCharges: CartCharges = {
            totalAmount: 0,
            items: []
        };

        if (this.selectedCustomizations.length > 0) {
            for (const offer of this.selectedCustomizations) {
                if (this.getCartCategory(offer) == 'SavingAndPromotions') {
                    const cartItem: CartItem = {
                        disclosure: offer.Choice?.CartDisclosure || '',
                        amount: offer.Choice?.Fee.Fee || 0,
                        name: offer.Choice?.CartName || offer.Choice?.Name || '',
                        choiceCode: offer.Choice?.ChoiceCode || '',
                        choice: offer.Choice,
                        customization: offer.Customization,
                        metadata: offer,
                        type: CartItemType.Promotion, //Make it promotion if it is in saving and promotion category
                        cartSectionType: CartSectionType.SavingsAndPromotions,
                        productType: ProductType.Internet
                    }
                    savingAndPromotionCharges.items.push(cartItem);
                    savingAndPromotionCharges.totalAmount += cartItem.amount;
                }
            }
        }

        return savingAndPromotionCharges;
    }

    private setupInternetCharges(): InternetCartData | undefined {
        const internetComponent = this.internetPdResponseService.getProductComponent();

        const internetCartData: InternetCartData = {
            name: internetComponent?.ComponentName || '',
            price: internetComponent?.Price || 0,
            promoPrice: internetComponent?.PromoPrice || 0,
            monthly: {
                totalAmount: internetComponent?.Price || 0,
                items: [],
                promotions: {
                    totalAmount: 0,
                    items: []
                }
            }
        };

        for (const offer of this.selectedCustomizations) {
            if (this.isABPChoice(offer)) {
                const cartItem = this.handleABPChoice(offer);
                internetCartData.monthly.promotions.items.push(cartItem);
                internetCartData.monthly.promotions.totalAmount += cartItem.amount;
            }
            else if (offer.Choice?.Fee?.FeeTerm === 'Monthly' && (offer.Choice.CartCategory == 'Internet' || (offer.Choice.CartCategory == null && offer.Customization.CartCategory == 'Internet'))) {
                const cartItem: CartItem = {
                    disclosure: offer.Choice?.CartDisclosure || '',
                    amount: offer.Choice?.Fee.Fee || 0,
                    name: offer.Choice?.CartName || offer.Choice?.Name || '',
                    choiceCode: offer.Choice?.ChoiceCode || '',
                    choice: offer.Choice,
                    customization: offer.Customization,
                    metadata: offer,
                    cartSectionType: CartSectionType.Monthly,
                    type: CartItemType.Fee,
                    productType: ProductType.Internet
                }

                if (cartItem.amount < 0) {
                    cartItem.type = CartItemType.Promotion; //Make it promotion if amount is negative
                    internetCartData.monthly.promotions.items.push(cartItem);
                    internetCartData.monthly.promotions.totalAmount += cartItem.amount;
                } else {
                    cartItem.type = CartItemType.Fee;
                    internetCartData.monthly.items.push(cartItem);
                    internetCartData.monthly.totalAmount += cartItem.amount;
                }

            }
        }

        internetCartData.monthly.totalAmount += internetCartData.monthly.promotions.totalAmount || 0;

        return internetCartData;
    }

    // Closing offer choices (e.g. closer coupons) carry no CartCategory; theirs is on the customization
    private getCartCategory(offer: CartSelectedCustomization): string | null | undefined {
        return offer.Choice.CartCategory
            ?? (isClosingOfferGroup(offer.Customization.DetailedGroupName) ? offer.Customization.CartCategory : null);
    }

    private isABPChoice(cust: CartSelectedCustomization): boolean {
        return isInternetAbpSubGroup(cust.Customization.SubGroupName) || isInternetAbpEnrolledChoiceCode(cust.Choice.ChoiceCode);
    }

    private handleABPChoice(offer: CartSelectedCustomization) {
        const cartItem: CartItem = {
            disclosure: offer.Choice?.CartDisclosure || '',
            amount: offer.Choice?.Fee.Fee || 0,
            name: offer.Choice?.CartName || offer.Choice?.Name || '',
            choiceCode: offer.Choice?.ChoiceCode || '',
            choice: offer.Choice,
            customization: offer.Customization,
            metadata: offer,
            cartSectionType: CartSectionType.Monthly,
            type: CartItemType.Promotion,
            productType: ProductType.Internet
        }

        const internetPayment = this.paymentService.internetPaymentProductResponse();
        if (internetPayment) {
            const autopayDetails = this.paymentService.getAutopayDetails(internetPayment);
            const autopayAmount = autopayDetails?.amount >= 0 ? autopayDetails.amount : -1;
            if (autopayAmount >= 0) {
                cartItem.amount = autopayAmount > 0 ? -autopayAmount : autopayAmount;
            }
        } else if (this.paymentService.internetPaymentCardType()?.toLowerCase() === 'debit') {
            cartItem.amount = cartItem.amount > 0 ? cartItem.amount - 5 : cartItem.amount + 5;
        } else if (this.paymentService.internetPaymentCardType()?.toLowerCase() === 'credit') {
            cartItem.amount = 0;
        }
        return cartItem;
    }

    isCartEmpty = computed(() => {
        return !this._cart() || this.selectedCustomizations.length === 0;
    })

    getTotalTodaysCharges(skipCharges: boolean = false): number {
        if (skipCharges) {
            return 0;
        }
        const cart = this.cart();
        const requiredTodaysCharges = cart?.todaysCharges?.items?.filter(item => item.isRequired === true || item.isRequired === null || item.isRequired === undefined);
        return requiredTodaysCharges?.reduce((total, item) => total + item.amount, 0) || 0;
    }

    getSelectedCustomizations(): CartSelectedCustomization[] {
        return this.selectedCustomizations;
    }

    hideInstallationTimeslots = computed(() => {
        const customizations = this.orderCustomizationsStateService.selectedCustomizations();
        return customizations.some(c => this.hideInstallationTimeslotChoiceCodes.has(c.choice?.ChoiceCode));
    })

}
